import { useEffect, useState } from "react";
import { Link } from "wouter";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { formatJamaicaDate } from "@/lib/datetime";
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, ExternalLink, Search, Users } from "lucide-react";
import {
  errorMessage, formatCount, formatMoney, getCustomerByUser, getCustomers, metricsUrls,
  type CustomerStatus, type CustomersResponse,
} from "@/lib/metrics";
import { EmptyState, ErrorState, StatusPill, TableSkeleton } from "./shared";

const STATUSES: Array<{ value: CustomerStatus; label: string }> = [
  { value: "all", label: "Everyone" },
  { value: "active", label: "Active" },
  { value: "trialing", label: "Trialing" },
  { value: "past_due", label: "Past due" },
  { value: "paused", label: "Paused" },
  { value: "churned", label: "Churned" },
];

type SortKey = "mrr" | "ltv" | "since" | "name";

const SORTABLE: Array<{ key: SortKey; label: string; align?: "right" }> = [
  { key: "name", label: "Customer" },
  { key: "mrr", label: "MRR", align: "right" },
  { key: "ltv", label: "LTV", align: "right" },
  { key: "since", label: "Since" },
];

const PAGE_SIZE = 25;

export default function CustomersTab({
  resolveUserId, onOpenCustomer,
}: {
  resolveUserId: string | null;
  onOpenCustomer: (customerId: string) => void;
}) {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<CustomerStatus>("all");
  const [sort, setSort] = useState<SortKey>("mrr");
  const [dir, setDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => { setQ(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const params = { q: q || undefined, status, sort, dir, page, pageSize: PAGE_SIZE };
  const { data, isLoading, isFetching, error } = useQuery<CustomersResponse>({
    queryKey: [metricsUrls.customers(params)],
    queryFn: () => getCustomers(params),
    placeholderData: keepPreviousData,
  });

  // Deep link from another admin page: /admin/metrics?tab=customers&user=<userId>
  const { data: resolved, error: resolveError } = useQuery<{ customerId: string }>({
    queryKey: [resolveUserId ? metricsUrls.customerByUser(resolveUserId) : "metrics-by-user-idle"],
    queryFn: () => getCustomerByUser(resolveUserId as string),
    enabled: !!resolveUserId,
  });

  useEffect(() => {
    if (resolved?.customerId) onOpenCustomer(resolved.customerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolved?.customerId]);

  useEffect(() => {
    if (resolveError) {
      toast({
        title: "No billing customer for that user",
        description: errorMessage(resolveError),
        variant: "destructive",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolveError]);

  const toggleSort = (key: SortKey) => {
    if (sort === key) setDir(dir === "asc" ? "desc" : "asc");
    else { setSort(key); setDir(key === "name" ? "asc" : "desc"); }
    setPage(1);
  };

  const rows = data?.rows || [];
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Card data-testid="tab-customers">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" />People insights</CardTitle>
            <CardDescription>Every billing customer, what they pay, what they have paid in total, and where to find them.</CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or email"
                className="pl-8 h-9 w-64"
                data-testid="input-customer-search"
              />
            </div>
            <Select value={status} onValueChange={(v) => { setStatus(v as CustomerStatus); setPage(1); }}>
              <SelectTrigger className="h-9 w-40" data-testid="select-customer-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? <TableSkeleton rows={8} cols={7} />
          : error ? <ErrorState error={error} />
            : rows.length === 0 ? (
              <EmptyState
                title="No customers match"
                hint={q || status !== "all"
                  ? "Try a different search or set the status filter back to Everyone."
                  : "Nothing has been mirrored from Stripe yet. Run a sync from the header above."}
              />
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {SORTABLE.map((c) => (
                          <TableHead key={c.key} className={c.align === "right" ? "text-right" : ""}>
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 hover:text-foreground"
                              onClick={() => toggleSort(c.key)}
                              data-testid={`sort-${c.key}`}
                            >
                              {c.label}
                              {sort === c.key ? (dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : null}
                            </button>
                          </TableHead>
                        ))}
                        <TableHead>Grade</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Last payment</TableHead>
                        <TableHead className="text-right">Failed</TableHead>
                        <TableHead className="w-24" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((row) => (
                        <TableRow key={row.customerId} data-testid={`customer-row-${row.customerId}`}>
                          <TableCell className="max-w-[16rem]">
                            <button
                              type="button"
                              className="font-medium text-primary hover:underline text-left truncate block"
                              onClick={() => onOpenCustomer(row.customerId)}
                            >
                              {row.name || row.email || row.customerId}
                            </button>
                            <div className="text-xs text-muted-foreground truncate">{row.email || "–"}</div>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{formatMoney(row.mrr)}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatMoney(row.ltv)}</TableCell>
                          <TableCell className="whitespace-nowrap text-sm">{row.since ? formatJamaicaDate(row.since) : "–"}</TableCell>
                          <TableCell>{row.grade != null ? <Badge variant="outline" className="text-[10px] py-0 h-4">G{row.grade}</Badge> : "–"}</TableCell>
                          <TableCell className="capitalize text-sm">
                            {row.plan || "–"}
                            {row.interval ? <span className="text-xs text-muted-foreground"> / {row.interval}</span> : null}
                            {row.subscriptions > 1 ? <span className="text-xs text-muted-foreground"> ×{row.subscriptions}</span> : null}
                          </TableCell>
                          <TableCell><StatusPill status={row.status} /></TableCell>
                          <TableCell className="whitespace-nowrap text-sm">{row.lastPaymentAt ? formatJamaicaDate(row.lastPaymentAt) : "–"}</TableCell>
                          <TableCell className={`text-right tabular-nums ${row.failedCharges > 0 ? "text-destructive" : ""}`}>{formatCount(row.failedCharges)}</TableCell>
                          <TableCell className="text-right">
                            {row.userId ? (
                              <Link href={`/admin/users?openUser=${row.userId}`} className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                                Open user <ExternalLink className="w-3 h-3" />
                              </Link>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                  <div className="text-xs text-muted-foreground">
                    {formatCount(total)} customer{total === 1 ? "" : "s"} · page {data?.page ?? page} of {pages}
                    {isFetching ? " · updating…" : ""}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="button-customers-prev">
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="button-customers-next">
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
      </CardContent>
    </Card>
  );
}
